import { Box, Button, Divider } from "@mui/material";
import React, { type ComponentProps, type ReactNode } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  PageLayout,
} from "../../api/types/objectTypes";
import PanelSection from "../common/layout/PanelSection";
import PanelSectionHeader from "../common/layout/PanelSectionHeader";
import FieldValuesContainer from "./FieldValuesContainer";
import type { EmailFieldEditMode } from "./FieldValuesContext";
import RelatedObjectsGroup from "./RelatedObjectsGroup";
import type { FieldValuesEditor } from "./useFieldValuesEditor";

interface Props {
  editor: FieldValuesEditor;
  objectType: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  fieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  allowEditing: boolean;
  emailFieldEditMode?: EmailFieldEditMode;
  isWaitingForValues?: boolean;
  fieldsAutoFillTooltips?: Record<string, string>;
  renderRelatedObjectsGroup?: (groupProps: ComponentProps<typeof RelatedObjectsGroup>) => ReactNode;
}

const FieldValuesEditorView = ({
  editor,
  objectType,
  businessCentralType,
  fields,
  cardLayout,
  fieldValues,
  fieldsConfidence,
  fieldsCitations,
  lookupObjects,
  allowEditing,
  emailFieldEditMode,
  isWaitingForValues,
  fieldsAutoFillTooltips,
  renderRelatedObjectsGroup,
}: Props) => {
  const {
    fieldValuesState,
    relatedObjectValuesState,
    relatedObjectsResp,
    relatedObjectsError,
    isFetchingRelatedObjects,
    lookupOptionsByObjectType,
    lookupOptionsOfRelatedObjectsByObjectType,
    relatedObjectFieldsByObjectType,
    validationErrorsByObjectType,
  } = editor;

  return (
    <PanelSection isLoading={editor.isBusy}>
      <PanelSectionHeader title="Details">
        {allowEditing && (
          <>
            <Button
              disabled={editor.areButtonsDisabled}
              onClick={editor.handleCancelClick}
              variant="text"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              loading={isWaitingForValues === true}
              disabled={editor.isSaveDisabled || isWaitingForValues === true}
              onClick={editor.handleSaveClick}
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
          </>
        )}
      </PanelSectionHeader>
      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2 }}>
        <FieldValuesContainer
          objectType={objectType}
          businessCentralType={businessCentralType}
          fields={fields}
          cardLayout={cardLayout}
          initialFieldValues={fieldValues}
          lookupObjects={lookupObjects}
          lookupOptionsByObjectType={lookupOptionsByObjectType}
          onAddLookupOption={editor.handleAddLookupOption}
          fieldsConfidence={fieldsConfidence}
          fieldsCitations={fieldsCitations}
          allowEditing={allowEditing}
          fieldValuesState={fieldValuesState}
          onFieldValuesStateChange={editor.handleFieldValuesStateChange}
          emailFieldEditMode={emailFieldEditMode}
          fieldsAutoFillTooltips={fieldsAutoFillTooltips}
        />

        {relatedObjectsError && (
          <>
            <Divider sx={{ my: 3 }} />
            <DataLoadingFailed
              title="Failed to load related objects"
              text={cardLayout.relatedObjectGrids?.map((x) => x.name).join(", ") ?? ""}
            />
          </>
        )}

        {!relatedObjectsError && relatedObjectsResp === undefined && <InlineLoader />}

        {relatedObjectsResp !== undefined &&
          Object.entries(relatedObjectValuesState.rowsByObjectType).map(([relatedObjectType, rows]) => {
            const layout = cardLayout.relatedObjectGrids?.find((x) => x.objectType === relatedObjectType);
            if (!layout) {
              return null;
            }

            const relatedObjectsData = relatedObjectsResp.relatedObjectsByType.find(
              (x) => x.objectType === relatedObjectType
            );
            if (!relatedObjectsData) {
              return null;
            }

            const lookupOptions =
              (lookupOptionsOfRelatedObjectsByObjectType
                ? lookupOptionsOfRelatedObjectsByObjectType[relatedObjectType]
                : undefined) ?? {};

            const validationErrors = validationErrorsByObjectType[relatedObjectType] ?? {};

            const groupProps: ComponentProps<typeof RelatedObjectsGroup> = {
              layout,
              fields: relatedObjectFieldsByObjectType[relatedObjectType] ?? [],
              objectPermissions: relatedObjectsData.objectPermissions,
              rows,
              setState: editor.handleRelatedObjectValuesChange,
              allowEditing,
              allowAddingAndDeletingRows: true,
              loading: isFetchingRelatedObjects,
              lookupOptions,
              onLookupOptionsNeeded: editor.handleLookupOptionsNeeded,
              validationErrors,
            };

            return (
              <React.Fragment key={relatedObjectType}>
                <Divider sx={{ my: 2 }} />
                {renderRelatedObjectsGroup ? (
                  renderRelatedObjectsGroup(groupProps)
                ) : (
                  <RelatedObjectsGroup {...groupProps} />
                )}
              </React.Fragment>
            );
          })}
      </Box>
    </PanelSection>
  );
};

export default FieldValuesEditorView;
